/**
 * The "developed" threshold — the one definition of section depth for every
 * canvas (office-hours decision, 2026-09-09, JJ-approved).
 *
 * Design principle: reward depth and return visits, never box-filling.
 * Dots measure DEPTH (this file); the WritingStreak measures SHOWING UP —
 * the two channels never mix. No overall completion percentage exists
 * anywhere: a group is "fully developed" when every REQUIRED section is
 * developed; optional fields never gate anything.
 *
 * A section is:
 *  - 'empty'      — nothing written (hollow dot)
 *  - 'progress'   — any non-empty content below the bar (half dot).
 *                   A note IS progress; it is just not development.
 *  - 'developed'  — a developed THOUGHT (solid amber dot):
 *                   substance ≥ 140 AND ≥ 2 sentences.
 *                   "Substance" is SCRIPT-WEIGHTED length: a Chinese or
 *                   Japanese character carries ~2.5 Latin characters of
 *                   meaning, an Ethiopic syllable ~2 — sixty Han characters
 *                   are a developed beat, not a fragment. Text with no
 *                   sentence marks at all qualifies at substance ≥ 100
 *                   (length is the only reliable signal there).
 *
 * Deterministic and client-computed on purpose: a writer can learn the bar
 * by feel, and dots update live while typing — no AI judging, no server
 * round-trip, no schema change.
 */

export const DEVELOPED_MIN_SUBSTANCE = 140;
export const DEVELOPED_MIN_SENTENCES = 2;
export const DEVELOPED_MIN_SUBSTANCE_NO_SENTENCE_MARKS = 100;

// Kana, Han (incl. ext-A + compat), halfwidth katakana, Hangul — dense
// logographic/syllabic scripts where one glyph ≈ a word or morpheme. The
// /u flag matches by CODE POINT, so an astral character (emoji, math symbol,
// CJK-Ext-B) can no longer match via a surrogate unit that happens to fall in
// a BMP range — only real BMP CJK/Kana/Hangul get the wide weight.
const WIDE_CHAR = /[぀-ヿ㐀-鿿豈-﫿ｦ-ﾟ가-힯]/u;
// Ge'ez/Ethiopic syllabary — one glyph ≈ a syllable.
const ETHIOPIC_CHAR = /[ሀ-፿]/u;

/** Script-weighted length: how much THOUGHT the text carries, not how many
 *  UTF-16 units it occupies. for..of yields whole code points, so an astral
 *  character counts once (weight 1), never per surrogate half. */
export function substanceLength(text) {
  let total = 0;
  for (const ch of (text || '')) {
    if (WIDE_CHAR.test(ch)) total += 2.5;
    else if (ETHIOPIC_CHAR.test(ch)) total += 2;
    else total += 1;
  }
  return total;
}

// Sentence enders across Pangaji's scripts: Latin . ! ? …, CJK 。！？,
// Ge'ez ። (Ethiopic full stop), Arabic ؟. A newline also separates thoughts
// (writers beat things out in lines).
const SENTENCE_ENDERS = /[.!?…。！？։።؟]+|\n+/g;

/** Count sentence-like units in a text. */
export function sentenceCount(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  const parts = trimmed
    .split(SENTENCE_ENDERS)
    .map((s) => s.trim())
    .filter(Boolean);
  // "One thought." → split gives ["One thought"] → 1 sentence; trailing
  // enders don't create phantom sentences.
  return parts.length;
}

/** 'empty' | 'progress' | 'developed' for one section's text. */
export function devLevel(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return 'empty';
  const substance = substanceLength(trimmed);
  const hasSentenceMarks = SENTENCE_ENDERS.test(trimmed);
  SENTENCE_ENDERS.lastIndex = 0; // /g regex — reset between uses
  if (!hasSentenceMarks) {
    // No sentence punctuation at all — judge by substance alone.
    return substance >= DEVELOPED_MIN_SUBSTANCE_NO_SENTENCE_MARKS ? 'developed' : 'progress';
  }
  if (substance >= DEVELOPED_MIN_SUBSTANCE && sentenceCount(trimmed) >= DEVELOPED_MIN_SENTENCES) {
    return 'developed';
  }
  return 'progress';
}

/**
 * Roll one group (an act's beats, a level's required questions) up to its
 * mark: 'developed' when EVERY required section is developed (the milestone
 * condition), 'progress' when anything is written, else 'empty'.
 * `texts` is the required sections' content only — pass optional fields
 * nowhere near this function.
 */
export function groupDevLevel(texts) {
  if (!texts || texts.length === 0) return 'empty';
  const levels = texts.map(devLevel);
  if (levels.every((l) => l === 'developed')) return 'developed';
  if (levels.some((l) => l !== 'empty')) return 'progress';
  return 'empty';
}

/**
 * "Levels 1–3 developed" — the Character Lab depth line. Never a percentage.
 * `groups` = [{ label, texts }] in display order; returns a human sentence
 * naming which groups are developed, or null when none are.
 */
export function depthSummary(groups) {
  const developed = groups.filter((g) => groupDevLevel(g.texts) === 'developed').map((g) => g.label);
  if (developed.length === 0) return null;
  return developed;
}
